import { activeLocale, hasMessage, t } from '@/i18n'
export class ApiError extends Error {
  constructor(public status: number, message: string, public errors?: Record<string, string[]>, public code?: string) { super(message) }
}

type ProblemResponse = {
  message?: string
  title?: string
  detail?: string
  code?: string
  messageKey?: string
  messageArguments?: Record<string, string | number>
  errors?: Record<string, string[] | string>
}

let csrfToken = ''
let csrfRequest: Promise<string> | undefined

async function getCsrfToken(refresh = false): Promise<string> {
  if (refresh) { csrfToken = ''; csrfRequest = undefined }
  if (csrfToken) return csrfToken
  csrfRequest ||= fetch('/api/security/csrf', { credentials: 'include', cache: 'no-store' })
    .then(async response => {
      if (!response.ok) throw new Error(t('api.prepareFailed'))
      const body = await response.json() as { token: string }
      csrfToken = body.token
      return csrfToken
    }).finally(() => { csrfRequest = undefined })
  return csrfRequest
}

function isUnsafe(method?: string) { return ['POST', 'PUT', 'PATCH', 'DELETE'].includes((method || 'GET').toUpperCase()) }

function validationMessages(errors?: ProblemResponse['errors']): string[] {
  if (!errors) return []
  return [...new Set(Object.values(errors).flatMap(value => Array.isArray(value) ? value : [value]).filter(Boolean))]
}

function problemMessage(body: ProblemResponse): string {
  if (body.messageKey && hasMessage(body.messageKey)) return t(body.messageKey, body.messageArguments || {})
  const validation = validationMessages(body.errors)
  if (body.message) return body.message
  if (validation.length) return validation.join(' ')
  if (body.detail) return body.detail
  if (body.title && body.title !== 'One or more validation errors occurred.') return body.title
  return t('api.saveFailed')
}

function mayBeStaleCsrf(response: Response, body: ProblemResponse): boolean {
  return response.status === 400 &&
    response.headers.get('content-type')?.includes('application/problem+json') === true &&
    validationMessages(body.errors).length === 0 &&
    !body.message &&
    !body.code
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const unsafe = isUnsafe(options.method)
  const requestedLocale = activeLocale.value
  const send = async (refreshToken = false) => fetch(path, {
    credentials: 'include',
    ...options,
    headers: {
      'X-Camplist-Locale': activeLocale.value,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(unsafe ? { 'X-Camplist-CSRF': await getCsrfToken(refreshToken) } : {}),
      ...options.headers
    }
  })
  let response = await send()
  let body = response.status === 204 ? {} : await response.json().catch(() => ({})) as ProblemResponse
  // Session rotation can invalidate an already-issued antiforgery token. Retry that
  // unclassified framework failure once, but never replay a form validation request.
  if (unsafe && mayBeStaleCsrf(response, body)) {
    response = await send(true)
    body = response.status === 204 ? {} : await response.json().catch(() => ({})) as ProblemResponse
  }
  if (response.status === 204) return undefined as T
  // Read responses can be refreshed after a switch. Never replay a mutation for language.
  if ((options.method || 'GET').toUpperCase() === 'GET' && requestedLocale !== activeLocale.value) return api<T>(path, options)
  if (!response.ok) {
    throw new ApiError(response.status, problemMessage(body), body.errors as Record<string, string[]> | undefined, body.code)
  }
  return body as T
}

export const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })
